import { apiGet, apiSend } from "./client.ts";
import {
  DEFAULT_PREVIEW_PREFS,
  normalizePreviewPrefs,
  type PreviewPrefs,
} from "../lib/previewSession.ts";
import {
  DEFAULT_PTZ_PREFS,
  normalizePtzPrefs,
  type PtzPrefs,
} from "../lib/ptzPrefs.ts";

export type PreviewPrefsPatch = Partial<PreviewPrefs>;

export type PtzPrefsPatch = Partial<PtzPrefs>;

function unwrapPrefs<T>(body: unknown): T | null {
  if (!body || typeof body !== "object") return null;
  const o = body as Record<string, unknown>;
  const inner = o.data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return inner as T;
  }
  return body as T;
}

/**
 * GET `/api/v1/me/preferences/preview` — falls back to documented defaults when
 * the route is absent or the session is not fully hydrated yet.
 */
export async function fetchPreviewPrefs(): Promise<PreviewPrefs> {
  try {
    const body = await apiGet<unknown>("/api/preferences/preview");
    const row = unwrapPrefs<Record<string, unknown>>(body);
    if (!row)
      return {
        ...DEFAULT_PREVIEW_PREFS,
        pipPosition: { ...DEFAULT_PREVIEW_PREFS.pipPosition },
      };
    const pipSharePct = Number(row.pipSharePct ?? row.pip_share_pct);
    const leftPct = Number(
      row.pipPositionLeftPct ?? row.pip_position_left_pct ?? row.leftPct,
    );
    const topPct = Number(
      row.pipPositionTopPct ?? row.pip_position_top_pct ?? row.topPct,
    );
    return normalizePreviewPrefs({
      pipSharePct,
      pipPosition: { leftPct, topPct },
    });
  } catch {
    return {
      ...DEFAULT_PREVIEW_PREFS,
      pipPosition: { ...DEFAULT_PREVIEW_PREFS.pipPosition },
    };
  }
}

export async function savePreviewPrefs(
  patch: PreviewPrefsPatch,
): Promise<void> {
  await apiSend("/api/preferences/preview", "PUT", patch);
}

export async function fetchPtzPrefs(): Promise<PtzPrefs> {
  try {
    const body = await apiGet<unknown>("/api/preferences/ptz");
    const row = unwrapPrefs<Record<string, unknown>>(body);
    if (!row) return { ...DEFAULT_PTZ_PREFS };
    const bufferMax = Number(row.bufferMax ?? row.buffer_max);
    return normalizePtzPrefs({ bufferMax });
  } catch {
    return { ...DEFAULT_PTZ_PREFS };
  }
}

export async function savePtzPrefs(patch: PtzPrefsPatch): Promise<void> {
  await apiSend("/api/preferences/ptz", "PUT", patch);
}
